import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { prepareRecordedAudioForUpload } from "../lib/audio_upload";
import { api } from "./client";
import type {
  CanonicalAlias,
  CanonicalAliasProposal,
  AdminUnidentifiedProduct,
  ChannelIdentity,
  ChannelIdentityChannel,
  Consigne,
  EvaluateConsignesResult,
  AgentAudioOut,
  AgentMessageOut,
  AttachableKind,
  CanonicalItemOption,
  ChatTurn,
  CommandeLineRow,
  CommandeRow,
  CreateLabelBody,
  CreateNoteBody,
  CreatePanneauBody,
  Espace,
  Label,
  Lieu,
  OpeningHoursDocument,
  Panneau,
  PanneauDetail,
  PanneauNote,
  Personne,
  PlanningWeekDocument,
  Counterparty,
  Supplier,
  VoiceInteractionMetricsIn,
} from "./types";

export type AttachableRow = Personne | Lieu | Espace;

export const qk = {
  panneaux: (conv: string) => ["panneaux", conv] as const,
  panneauxArchived: (conv: string) => ["panneaux", "archived", conv] as const,
  panneau: (id: string) => ["panneau", id] as const,
  labels: (org: string) => ["labels", org] as const,
  suppliers: (org: string) => ["suppliers", org] as const,
  counterparties: (org: string) => ["counterparties", org] as const,
  attachables: (kind: AttachableKind, org: string) => [kind, org] as const,
  history: (conv: string) => ["history", conv] as const,
};

export function usePanneaux(conv: string) {
  // The `conv` param is now used only as a Tanstack Query cache key
  // (so org-switches invalidate cleanly). The server derives conv from
  // the session cookie — we don't send it on the URL anymore.
  return useQuery({
    queryKey: qk.panneaux(conv),
    queryFn: () => api<Panneau[]>(`/api/panneaux`),
    enabled: !!conv,
    staleTime: 5_000,
  });
}

export function usePanneau(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.panneau(id) : ["panneau", "noop"],
    queryFn: () => api<PanneauDetail>(`/api/panneaux/${id}`),
    enabled: !!id,
    staleTime: 1_000,
  });
}

export function useCreatePanneau() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePanneauBody) =>
      api<Panneau>("/api/panneaux", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      // Invalidate every cached panneau list — only one should be
      // active at a time anyway (the current org's).
      qc.invalidateQueries({ queryKey: ["panneaux"] });
    },
  });
}

export function useArchivePanneau(conv: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/panneaux/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: qk.panneaux(conv) });
      qc.invalidateQueries({ queryKey: qk.panneauxArchived(conv) });
      qc.removeQueries({ queryKey: qk.panneau(id) });
    },
  });
}

export function useArchivedPanneaux(conv: string) {
  // Powers the Archivés modal + the count chip in the header.
  // staleTime is short because the count is shown in the header chip
  // and a stale count of 0 (when there's actually 1) hides the chip.
  return useQuery({
    queryKey: qk.panneauxArchived(conv),
    queryFn: () => api<Panneau[]>(`/api/panneaux/archived`),
    enabled: !!conv,
    staleTime: 1_000,
  });
}

export function useRestorePanneau(conv: string) {
  // Reused by both the undo-toast (within 5s of close) and the
  // Archivés modal's Restaurer button.
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<Panneau>(`/api/panneaux/${id}/restore`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneaux(conv) });
      qc.invalidateQueries({ queryKey: qk.panneauxArchived(conv) });
    },
  });
}

export function useDeletePanneauPermanently(conv: string) {
  // Hard delete from the Archivés modal — requires the panneau to
  // already be archived (backend 409s otherwise).
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/panneaux/${id}/permanent`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: qk.panneauxArchived(conv) });
      qc.removeQueries({ queryKey: qk.panneau(id) });
    },
  });
}

export function useUpdatePanneau(panneauId: string, conv: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; config?: Record<string, unknown> }) =>
      api<Panneau>(`/api/panneaux/${panneauId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
      qc.invalidateQueries({ queryKey: qk.panneaux(conv) });
    },
  });
}

export function useValidateAll(panneauId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ committed: string[]; skipped: string[] }>(
        `/api/panneaux/${panneauId}/validate-all`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

/**
 * Per-proposal validate (commit one specific pending proposal).
 *
 * Pending: backend endpoint `POST /api/panneaux/{id}/validate/{pid}` is
 * planned in lockstep with the write-tools rollout but may not exist
 * yet. UI consumers should fall back to `useValidateAll` if a 404
 * surfaces. Wired now so chip-level "Valider" ticks have the right
 * shape against the panneau cache invalidation.
 */
export function useValidateProposal(panneauId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: string) =>
      api<{ committed: string[]; skipped: string[] }>(
        `/api/panneaux/${panneauId}/validate/${proposalId}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useRejectProposal(panneauId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: string) =>
      api<void>(`/api/panneaux/${panneauId}/reject/${proposalId}`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useLabels(organisation: string) {
  return useQuery({
    queryKey: qk.labels(organisation),
    queryFn: () =>
      // Phase 5: server derives org from session; we keep it in the
      // cache key so org-switches invalidate cleanly.
      api<Label[]>(`/api/labels`),
    staleTime: 30_000,
  });
}

export function useCreateLabel(organisation: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLabelBody) =>
      api<Label>("/api/labels", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.labels(organisation) });
    },
  });
}

export function useAttachLabel(panneauId: string, organisation: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labelId: string) =>
      api<void>(`/api/panneaux/${panneauId}/labels/${labelId}`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
      qc.invalidateQueries({ queryKey: qk.labels(organisation) });
    },
  });
}

export function useDetachLabel(panneauId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labelId: string) =>
      api<void>(`/api/panneaux/${panneauId}/labels/${labelId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useUpdateLabel(organisation: string, panneauId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      color?: string;
      label_type?: import("./types").LabelType;
    }) =>
      api<Label>(`/api/labels/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.labels(organisation) });
      if (panneauId) qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useDeleteLabel(organisation: string, panneauId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/labels/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.labels(organisation) });
      if (panneauId) qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

// ─────────────────────── Notes ───────────────────────

export function useCreateNote(panneauId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateNoteBody) =>
      api<PanneauNote>(`/api/panneaux/${panneauId}/notes`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function usePatchNote(panneauId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      is_definitive,
      body,
    }: {
      id: string;
      is_definitive?: boolean;
      body?: string;
    }) => {
      const payload: Record<string, unknown> = {};
      if (is_definitive !== undefined) payload.is_definitive = is_definitive;
      if (body !== undefined) payload.body = body;
      return api<PanneauNote>(`/api/notes/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useDeleteNote(panneauId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/notes/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

// ─────────────────────── Attachables (personnes/espaces/lieux) ───────────

export function useAttachables(kind: AttachableKind, organisation: string) {
  return useQuery({
    queryKey: qk.attachables(kind, organisation),
    queryFn: () =>
      api<AttachableRow[]>(
        // Phase 5: server derives org from session.
        `/api/${kind}`,
      ),
    staleTime: 30_000,
  });
}

export function useCreateAttachable(
  kind: AttachableKind,
  organisation: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<AttachableRow>(`/api/${kind}`, {
        method: "POST",
        // Phase 5: server derives organisation from session.
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.attachables(kind, organisation) });
    },
  });
}

export function useAttachToPanneau(
  kind: AttachableKind,
  panneauId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api<void>(`/api/panneaux/${panneauId}/${kind}/${itemId}`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useDetachFromPanneau(
  kind: AttachableKind,
  panneauId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api<void>(`/api/panneaux/${panneauId}/${kind}/${itemId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useUpdateAttachable(
  kind: AttachableKind,
  organisation: string,
  panneauId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<AttachableRow>(`/api/${kind}/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.attachables(kind, organisation) });
      if (panneauId) qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useDeleteAttachable(
  kind: AttachableKind,
  organisation: string,
  panneauId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/${kind}/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.attachables(kind, organisation) });
      if (panneauId) qc.invalidateQueries({ queryKey: qk.panneau(panneauId) });
    },
  });
}

export function useSendAgentMessage(conv: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      api<AgentMessageOut>("/api/agent/message", {
        method: "POST",
        body: JSON.stringify({ text }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["panneau"] });
      qc.invalidateQueries({ queryKey: qk.panneaux(conv) });
      qc.invalidateQueries({ queryKey: qk.history(conv) });
    },
  });
}

export function useSendAgentAudio(conv: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      blob,
      mimeType,
    }: {
      blob: Blob;
      mimeType?: string;
    }) => {
      const preparedPromise = prepareRecordedAudioForUpload(blob, mimeType);
      return preparedPromise.then((prepared) => {
        const fd = new FormData();
        fd.append("file", prepared.blob, `ptt.${prepared.extension}`);
        return api<AgentAudioOut>("/api/agent/audio", {
          method: "POST",
          body: fd,
        });
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["panneau"] });
      qc.invalidateQueries({ queryKey: qk.panneaux(conv) });
      qc.invalidateQueries({ queryKey: qk.history(conv) });
    },
  });
}

export function useRecordVoiceInteraction() {
  return useMutation({
    mutationFn: (body: VoiceInteractionMetricsIn) =>
      api<void>("/api/agent/performance", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

export function useAgentHistory(conv: string, enabled: boolean = true) {
  return useQuery({
    queryKey: qk.history(conv),
    queryFn: () => api<ChatTurn[]>(`/api/agent/history?limit=100`),
    enabled: enabled && !!conv,
    staleTime: 5_000,
  });
}

// ── Commandes (Phase 6) ────────────────────────────────────────────────

export function useCommandes() {
  return useQuery<CommandeRow[]>({
    queryKey: ["commandes"],
    queryFn: () => api<CommandeRow[]>("/api/commandes"),
    staleTime: 10_000,
  });
}

export function useCommandeLines(commandeId: string | null) {
  return useQuery<CommandeLineRow[]>({
    queryKey: ["commandeLines", commandeId],
    queryFn: () =>
      api<CommandeLineRow[]>(`/api/commandes/${commandeId}/lines`),
    enabled: !!commandeId,
    staleTime: 10_000,
  });
}

export function useSuppliers(organisation: string) {
  return useQuery<Supplier[]>({
    queryKey: qk.suppliers(organisation),
    queryFn: () => api<Supplier[]>("/api/fournisseurs"),
    enabled: !!organisation,
    staleTime: 10_000,
  });
}

export function useCounterparties(organisation: string) {
  return useQuery<Counterparty[]>({
    queryKey: qk.counterparties(organisation),
    queryFn: () => api<Counterparty[]>("/api/admin/contreparties"),
    enabled: !!organisation,
    staleTime: 10_000,
  });
}

export function useCreateCounterparty(organisation: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      contact_name?: string | null;
      contact_email?: string | null;
      contact_phone?: string | null;
      notes?: string | null;
      is_customer?: boolean;
      is_supplier?: boolean;
      aliases?: string[];
    }) =>
      api<Counterparty>("/api/admin/contreparties", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.counterparties(organisation) });
    },
  });
}

export function useUpdateCounterparty(organisation: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      contact_name?: string | null;
      contact_email?: string | null;
      contact_phone?: string | null;
      notes?: string | null;
      is_customer?: boolean;
      is_supplier?: boolean;
      is_active?: boolean;
      aliases?: string[];
    }) =>
      api<Counterparty>(`/api/admin/contreparties/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.counterparties(organisation) });
    },
  });
}

export function useCreateSupplier(organisation: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      contact_name?: string | null;
      contact_email?: string | null;
      contact_phone?: string | null;
      notes?: string | null;
    }) =>
      api<Supplier>("/api/fournisseurs", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.suppliers(organisation) });
    },
  });
}

// ── Admin: unidentified products (Issue #14) ─────────────────────────

export type UnidentifiedStatus = "open" | "resolved" | "deleted" | "all";

export function useAdminUnidentifiedProducts(
  status: UnidentifiedStatus = "open",
  query: string = "",
) {
  return useQuery<AdminUnidentifiedProduct[]>({
    queryKey: ["admin", "unidentified-products", status, query],
    queryFn: () => {
      const params = new URLSearchParams({ status });
      if (query.trim()) params.set("query", query.trim());
      return api<AdminUnidentifiedProduct[]>(
        `/api/admin/unidentified-products?${params.toString()}`,
      );
    },
    staleTime: 5_000,
  });
}

export function useAdminCanonicalItems(query: string = "") {
  return useQuery<CanonicalItemOption[]>({
    queryKey: ["admin", "canonical-items", query],
    queryFn: () => {
      const params = new URLSearchParams();
      if (query.trim()) params.set("query", query.trim());
      const qs = params.toString();
      return api<CanonicalItemOption[]>(
        `/api/admin/canonical-items${qs ? `?${qs}` : ""}`,
      );
    },
    staleTime: 30_000,
  });
}

export function useAdminCanonicalAliasProposals(enabled: boolean = true) {
  return useQuery<CanonicalAliasProposal[]>({
    queryKey: ["admin", "canonical-alias-proposals"],
    queryFn: () => api<CanonicalAliasProposal[]>("/api/admin/canonical-alias-proposals"),
    staleTime: 5_000,
    enabled,
  });
}

export function useAdminCanonicalAliases(query: string = "") {
  return useQuery<CanonicalAlias[]>({
    queryKey: ["admin", "canonical-aliases", query],
    queryFn: () => {
      const params = new URLSearchParams();
      if (query.trim()) params.set("query", query.trim());
      const qs = params.toString();
      return api<CanonicalAlias[]>(
        `/api/admin/canonical-aliases${qs ? `?${qs}` : ""}`,
      );
    },
    staleTime: 5_000,
  });
}

export function useAdminOpeningHours(organisationId: string) {
  return useQuery<OpeningHoursDocument>({
    queryKey: ["admin-opening-hours", organisationId],
    queryFn: () => api<OpeningHoursDocument>("/api/admin/opening-hours"),
    enabled: !!organisationId,
  });
}

export function useSaveAdminOpeningHours(organisationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OpeningHoursDocument) =>
      api<OpeningHoursDocument>("/api/admin/opening-hours", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin-opening-hours", organisationId] }),
  });
}

export function useAdminConsignes() {
  return useQuery<Consigne[]>({
    queryKey: ["admin", "consignes"],
    queryFn: () => api<Consigne[]>("/api/admin/consignes"),
    staleTime: 5_000,
  });
}

export function useCreateAdminConsigne() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { text_fr: string }) =>
      api<Consigne>("/api/admin/consignes", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "consignes"] });
    },
  });
}

export function useUpdateAdminConsigne() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text_fr }: { id: string; text_fr: string }) =>
      api<Consigne>(`/api/admin/consignes/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ text_fr }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "consignes"] });
    },
  });
}

export function useToggleAdminConsigne() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enable }: { id: string; enable: boolean }) =>
      api<Consigne>(`/api/admin/consignes/${id}/${enable ? "enable" : "disable"}`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "consignes"] });
    },
  });
}

export function useDeleteAdminConsigne() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/admin/consignes/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "consignes"] });
    },
  });
}

export function useEvaluateAdminConsignes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<EvaluateConsignesResult>("/api/admin/consignes/evaluate", {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "consignes"] });
      qc.invalidateQueries({ queryKey: ["panneaux"] });
    },
  });
}

export function useEvaluateAdminConsigne() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<EvaluateConsignesResult>(`/api/admin/consignes/${id}/evaluate`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "consignes"] });
      qc.invalidateQueries({ queryKey: ["panneaux"] });
    },
  });
}

export interface PatchUnidentifiedBody {
  action: "resolve" | "promote" | "delete";
  canonical_id?: string;
  name_fr?: string;
  category?: string;
  unit?: string;
  aliases?: string[];
  force?: boolean;
}

export function usePatchUnidentifiedProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: { id: string } & PatchUnidentifiedBody) =>
      api<{ ok: true; canonical_id?: string }>(
        `/api/admin/unidentified-products/${id}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "unidentified-products"] });
      qc.invalidateQueries({ queryKey: ["admin", "canonical-items"] });
    },
  });
}

export function useValidateCanonicalAliasProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: string) =>
      api<void>(`/api/admin/canonical-alias-proposals/${proposalId}/validate`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "canonical-alias-proposals"] });
      qc.invalidateQueries({ queryKey: ["admin", "canonical-aliases"] });
    },
  });
}

export function useRejectCanonicalAliasProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: string) =>
      api<void>(`/api/admin/canonical-alias-proposals/${proposalId}/reject`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "canonical-alias-proposals"] });
    },
  });
}

export function useDeleteCanonicalAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aliasId: string) =>
      api<void>(`/api/admin/canonical-aliases/${aliasId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "canonical-aliases"] });
      qc.invalidateQueries({ queryKey: ["admin", "canonical-alias-proposals"] });
    },
  });
}

export function useUpdateSupplier(organisation: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      contact_name?: string | null;
      contact_email?: string | null;
      contact_phone?: string | null;
      notes?: string | null;
      is_active?: boolean;
    }) =>
      api<Supplier>(`/api/fournisseurs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.suppliers(organisation) });
    },
  });
}

// ── Admin: planning prototype (issue #15) ────────────────────────────
// Server derives organisation from the session cookie, so the hook only
// needs the week_start. The cache key tags the org-switch implicitly
// via the `me` invalidation chain.

export function useAdminPlanningWeek(weekStart: string) {
  return useQuery<PlanningWeekDocument>({
    queryKey: ["admin-planning", weekStart],
    queryFn: () =>
      api<PlanningWeekDocument>(
        `/api/admin/planning?week_start=${encodeURIComponent(weekStart)}`,
      ),
    enabled: !!weekStart,
  });
}

export function useSaveAdminPlanningWeek(weekStart: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PlanningWeekDocument) =>
      api<PlanningWeekDocument>(
        `/api/admin/planning?week_start=${encodeURIComponent(weekStart)}`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin-planning", weekStart] }),
  });
}

// ── Admin: channel identities (Teams / WhatsApp bindings) ────────────

export function useAdminChannelIdentities(profilId: string | null) {
  return useQuery<ChannelIdentity[]>({
    queryKey: ["admin", "channel-identities", profilId ?? "none"],
    queryFn: () => {
      const params = new URLSearchParams({ profil_id: profilId! });
      return api<ChannelIdentity[]>(
        `/api/admin/channel-identities?${params.toString()}`,
      );
    },
    enabled: !!profilId,
    staleTime: 5_000,
  });
}

export function useCreateAdminChannelIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      profil_id: string;
      channel: ChannelIdentityChannel;
      external_id: string;
    }) =>
      api<ChannelIdentity>("/api/admin/channel-identities", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (row) =>
      qc.invalidateQueries({
        queryKey: ["admin", "channel-identities", row.profil_id],
      }),
  });
}

export function useDeleteAdminChannelIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; profilId: string }) =>
      api<void>(`/api/admin/channel-identities/${id}`, { method: "DELETE" }),
    onSuccess: (_data, { profilId }) =>
      qc.invalidateQueries({
        queryKey: ["admin", "channel-identities", profilId],
      }),
  });
}
